import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, CheckIcon } from 'lucide-react';
import { Logo } from '../components/brand/Logo';
import { Seal } from '../components/brand/Seal';
import { carePaths } from '../data/carePaths';
import { useSymptomSelection } from '../hooks/useSymptomSelection';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { useBrand } from '../contexts/BrandContext';
interface Question {
  id: string;
  prompt: string;
  help: string;
  options: string[];
  allowSkip: boolean;
}
const questions: Question[] = [{
  id: 'bothering',
  prompt: 'What’s bothering you most right now?',
  help: 'Just the loudest one. You will get to say the rest later, and you do not need the right medical word for it.',
  options: ['I can’t sleep', 'The mood, the rage, the fog', 'Hot flushes and sweats', 'My body has changed shape', 'Sex hurts, or desire has gone', 'Something else entirely'],
  allowSkip: false
}, {
  id: 'age',
  prompt: 'Roughly how old are you?',
  help: 'Only to give your clinician a starting point. There is no answer here that makes you too young.',
  options: ['Under 40', '40 to 44', '45 to 50', '51 to 55', 'Over 55'],
  allowSkip: true
}, {
  id: 'cycle',
  prompt: 'What are your periods doing?',
  help: 'This is worth more than any blood test. An honest “I have no idea” is a completely fine answer.',
  options: ['Still regular', 'Shorter or closer together', 'Irregular or skipping', 'Heavier than they were', 'Stopped over 12 months ago', 'I don’t know'],
  allowSkip: true
}, {
  id: 'history',
  prompt: 'Has anyone looked at this with you before?',
  help: 'We ask because most women arrive having already been turned away, and we would rather know than make you repeat it.',
  options: ['No, this is the first time', 'Yes, and I was dismissed', 'Yes, and I was offered an antidepressant', 'Yes, I have been treated before'],
  allowSkip: true
}, {
  id: 'want',
  prompt: 'What would you like to come out of this?',
  help: 'There is no wrong answer, including “I just want someone to tell me what is happening”.',
  options: ['To know what is happening to me', 'To sleep again', 'To discuss hormone therapy', 'Non-hormonal options', 'I’m not sure yet'],
  allowSkip: true
}];
export function Start() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [done, setDone] = useState(false);
  const {
    selected
  } = useSymptomSelection();
  const {
    brand
  } = useBrand();
  useDocumentMeta({
    title: `Start with one question — ${brand.name}`,
    description: 'Five questions, one at a time. Nothing is charged and nothing is prescribed until a clinician has read your answers and spoken to you.'
  });
  const question = questions[step];
  const isLast = step === questions.length - 1;
  const answer = (value: string) => {
    setAnswers(current => ({
      ...current,
      [question.id]: value
    }));
    advance();
  };
  const advance = () => {
    if (isLast) {
      setDone(true);
      return;
    }
    setStep(current => current + 1);
  };
  if (done) {
    return <Complete answers={answers} symptomCount={selected.length} data-id="element-654" data-mp="3t3gh2igtgii2" />;
  }
  return <main className="w-full bg-cream" data-id="element-655" data-mp="z4f9x4lodrl">
      <div className="mx-auto max-w-3xl px-6 py-16 md:py-24" data-id="element-656" data-mp="y2toypyk8vm">
        <div className="flex items-center justify-between gap-6" data-id="element-657" data-mp="3j50mu6mhst4c">
          <Logo className="text-lg" markClassName="text-brick" data-id="element-658" data-mp="3j6lkw680mu6y" />
          <p className="text-sm text-ink-soft" data-id="element-659" data-mp="3o1r3av27lqqa">
            {step + 1} of {questions.length} · nothing is saved until you finish
          </p>
        </div>

        <div className="mt-8 flex gap-2" aria-hidden="true" data-id="element-660" data-mp="2coolxxsacx52">
          {questions.map((item, index) => <span key={item.id} className={`h-0.5 flex-1 transition-colors duration-200 ease-brand ${index <= step ? 'bg-brick' : 'bg-ink/15'}`} data-id="element-661" data-mp="2ry21yon2mmyk" data-mpi={index} />)}
        </div>

        <AnimatePresence mode="wait" data-id="element-662" data-mp="2j5qglh6tdppg">
          <motion.section key={question.id} initial={{
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -6
        }} transition={{
          duration: 0.2,
          ease: [0.23, 1, 0.32, 1]
        }} className="mt-14" aria-labelledby={`q-${question.id}`} data-id="element-663" data-mp="2e6ng8a1s47nt">
            <h1 id={`q-${question.id}`} className="font-serif text-[2.25rem] leading-[1.08] text-ink md:text-[3rem]" data-id="element-664" data-mp="31uetpgz22qqo">
              {question.prompt}
            </h1>
            <p className="mt-5 max-w-xl leading-relaxed text-ink-soft" data-id="element-665" data-mp="1qi4aa167c6yw">{question.help}</p>

            <ul className="mt-10 flex flex-col gap-3" data-id="element-666" data-mp="1lg57otiade30">
              {question.options.map(option => <li key={option} data-id="element-667" data-mp="1bfmegvqlf6nu">
                  <button type="button" onClick={() => answer(option)} className="group flex w-full items-center justify-between gap-6 rounded-brand border border-ink/20 bg-cream-light px-6 py-5 text-left transition-[border-color,background-color] duration-150 ease-brand hover:border-brick hover:bg-cream-deep" data-id="element-668" data-mp="1gcw2c6yc3a9k">
                    <span className="font-serif text-xl leading-snug text-ink" data-id="element-669" data-mp="1z3dpzx4b7fo2">{option}</span>
                    <ArrowRightIcon className="h-5 w-5 shrink-0 text-ink-muted transition-[transform,color] duration-150 ease-brand group-hover:translate-x-1 group-hover:text-brick" aria-hidden="true" data-id="element-670" data-mp="1z56v37z9xrty" />
                  </button>
                </li>)}
            </ul>

            <div className="mt-10 flex flex-wrap items-center justify-between gap-4" data-id="element-671" data-mp="eok2jtvxod8g">
              {step > 0 ? <button type="button" onClick={() => setStep(current => current - 1)} className="inline-flex items-center gap-2 text-sm text-ink-soft transition-colors duration-150 ease-brand hover:text-ink" data-id="element-672" data-mp="6ncrz4bmw1ck">
                  <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-673" data-mp="bkw49yoru41v" />
                  Back
                </button> : <span data-id="element-674" data-mp="pjm61rmjcrwk" />}
              {question.allowSkip && <button type="button" onClick={advance} className="text-sm text-ink-soft underline-offset-4 transition-colors duration-150 ease-brand hover:text-brick hover:underline" data-id="element-675" data-mp="kmc5bftp4ttf">
                  Skip this one
                </button>}
            </div>
          </motion.section>
        </AnimatePresence>

        <p className="rule mt-16 pt-6 text-sm leading-relaxed text-ink-soft" data-id="element-676" data-mp="14c60m3vmdr2e">
          You can close this and come back. Nothing is charged and nothing is prescribed until a
          clinician has read your answers and spoken with you.
        </p>
      </div>
    </main>;
}
function Complete({
  answers,
  symptomCount
}: {
  answers: Record<string, string>;
  symptomCount: number;
}) {
  const suggested = carePaths[0];
  const answered = Object.entries(answers);
  return <main className="w-full bg-cream" data-id="element-677" data-mp="147cmgekhilsl">
      <motion.div initial={{
      opacity: 0,
      y: 6
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.25,
      ease: [0.23, 1, 0.32, 1]
    }} className="mx-auto max-w-3xl px-6 py-20 md:py-28" data-id="element-678" data-mp="146ksf86jcw4f">
        <div className="flex items-start justify-between gap-8" data-id="element-679" data-mp="z8t1ohhmng1t">
          <div data-id="element-680" data-mp="z788owayo562">
            <p className="text-[0.6875rem] uppercase tracking-label text-brick" data-id="element-681" data-mp="l9jkbehoqfw9">That’s it</p>
            <h1 className="mt-5 font-serif text-[2.5rem] leading-[1.05] text-ink md:text-[3.5rem]" data-id="element-682" data-mp="lbw3fuz6iwaw">
              Someone will actually read this.
            </h1>
            <p className="mt-7 text-lg leading-relaxed text-ink-soft" data-id="element-683" data-mp="15q4a8q3xepbj">
              A Menopause Society–certified clinician reviews your answers before your first
              appointment, so you will not be starting from the beginning again. You will hear back
              within one working day with times to choose from.
            </p>
            <p className="mt-5 text-lg leading-relaxed text-ink-soft" data-id="element-684" data-mp="mrs134d0lffy">
              Nothing is charged today. There is no next step you need to take right now.
            </p>
          </div>
          <Seal className="hidden h-28 w-28 shrink-0 text-brick/70 sm:block" data-id="element-685" data-mp="d1ynxyx84s9l" />
        </div>

        <div className="mt-14 rounded-brand border border-ink/15 bg-cream-light p-8" data-id="element-686" data-mp="czm5lknk0fw0">
          <p className="text-[0.6875rem] uppercase tracking-label text-ink-muted" data-id="element-687" data-mp="8yagdg8ldwoc">
            What your clinician will see
          </p>
          <dl className="mt-6" data-id="element-688" data-mp="3v71xbvu83qft">
            {answered.map(([key, value]) => <div key={key} className="rule flex flex-wrap justify-between gap-4 py-4 first:border-t-0 first:pt-0" data-id="element-689" data-mp="2apj9dcyexw3v">
                <dt className="text-ink-soft" data-id="element-690" data-mp="2fo4adelhojtd">
                  {questions.find(question => question.id === key)?.prompt}
                </dt>
                <dd className="text-ink" data-id="element-691" data-mp="2koi92617aheq">{value}</dd>
              </div>)}
            {symptomCount > 0 && <div className="rule flex flex-wrap justify-between gap-4 py-4" data-id="element-692" data-mp="2omzgyrsg3vx7">
                <dt className="text-ink-soft" data-id="element-693" data-mp="1s0w68zzsrgsx">From your symptom decoder</dt>
                <dd className="text-ink" data-id="element-694" data-mp="1wyxoxjtb9e3v">
                  {symptomCount} symptom{symptomCount === 1 ? '' : 's'} selected
                </dd>
              </div>}
          </dl>
        </div>

        <div className="mt-12 flex flex-col gap-4" data-id="element-695" data-mp="25r9bgulb0hek">
          <p className="font-serif text-2xl leading-snug text-ink" data-id="element-696" data-mp="198tlbcg4hecv">
            While you wait, there is no homework. But if you want something:
          </p>
          <ul className="flex flex-col" data-id="element-697" data-mp="1d8c7ebjojvha">
            <li className="rule py-4" data-id="element-698" data-mp="1ku2igao7l6n2">
              <Link to="/symptoms" className="text-brick underline-offset-4 hover:underline" data-id="element-699" data-mp="1kt0wvc9kh1r3">
                Use the symptom decoder
              </Link>
              <span className="text-ink-soft" data-id="element-700" data-mp="1tuqnifvmjwnt">
                {' '}
                — it produces a summary your clinician can read before you meet.
              </span>
            </li>
            <li className="rule py-4" data-id="element-701" data-mp="123b2urdmok08">
              <Link to="/circles" className="text-brick underline-offset-4 hover:underline" data-id="element-702" data-mp="124cf2z6drcjj">
                Look at the circles
              </Link>
              <span className="text-ink-soft" data-id="element-703" data-mp="162jv1rt0s9to"> — the overnight thread is open to anyone.</span>
            </li>
            <li className="rule py-4" data-id="element-704" data-mp="1fupnt3r3iv9x">
              <Link to={`/care/${suggested.slug}`} className="text-brick underline-offset-4 hover:underline" data-id="element-705" data-mp="1fvr8lzxwyw4u">
                Read about the care paths
              </Link>
              <span className="text-ink-soft" data-id="element-706" data-mp="ncu3ddkk045z"> — no commitment attached to any of them.</span>
            </li>
          </ul>
        </div>

        <p className="rule mt-14 flex items-start gap-3 pt-6 text-sm leading-relaxed text-ink-soft" data-id="element-707" data-mp="avt0wmzo2db5">
          <CheckIcon className="mt-1 h-4 w-4 shrink-0 text-brick" strokeWidth={2} aria-hidden="true" data-id="element-708" data-mp="5wpylab2ml6v" />
          No card was taken, no subscription started, and no marketing sequence begins now.
        </p>
      </motion.div>
    </main>;
}